import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  Storefront, CalendarDots, Package, GearSix, ArrowRight, Confetti, X
} from '@phosphor-icons/react';
import { useTenant } from '../contexts/TenantContext';

const API = process.env.REACT_APP_BACKEND_URL;

// Confetti icon doesn't exist in all versions of @phosphor-icons/react; render
// inline so the welcome screen never blows up on an icon-set mismatch.
const ConfettiIconWrap = () => (
  <Confetti size={14} weight="fill" className="text-[#9D4CDD]" />
);

/**
 * ShopWelcomePage — landing screen for newly-registered shops.
 * Renders 3 high-impact CTAs (add first event, add inventory, complete profile)
 * before dumping the owner into the empty dashboard. Skip → goes to /shop/{id}.
 */
export default function ShopWelcomePage() {
  const navigate = useNavigate();
  const { shopId } = useParams();
  const { refreshContexts } = useTenant();
  const [shop, setShop] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    // RBAC tenant + shop_owner role were just provisioned server-side during
    // /auth/register-as-shop. We MUST refresh contexts so PortalGuard sees
    // "shop_portal" before the user clicks any of the CTAs below.
    let mounted = true;
    (async () => {
      try { await refreshContexts(); } catch {}
      try {
        const { data } = await axios.get(`${API}/api/shop-portal/${shopId}/profile`, { withCredentials: true });
        if (mounted) setShop(data);
      } catch {}
      if (mounted) setReady(true);
    })();
    return () => { mounted = false; };
  }, [shopId, refreshContexts]);

  const ctas = [
    {
      key: 'event',
      icon: CalendarDots,
      color: '#00F0FF',
      title: 'Add your first event',
      sub: 'Game nights, tournaments, demos — fills your calendar',
      route: `/shop/${shopId}/events`,
    },
    {
      key: 'inventory',
      icon: Package,
      color: '#9D4CDD',
      title: 'Add inventory',
      sub: 'Stock games so players can discover what you carry',
      route: `/shop/${shopId}/game-library`,
    },
    {
      key: 'profile',
      icon: GearSix,
      color: '#F59E0B',
      title: 'Complete your profile',
      sub: 'Hours, photos, address, policies',
      route: `/shop/${shopId}/profile`,
    },
  ];

  return (
    <div className="min-h-screen bg-[#06060A] text-white" data-testid="shop-welcome-page">
      {/* Skip button */}
      <div className="absolute top-4 right-4 z-10">
        <button
          onClick={() => navigate(`/shop/${shopId}`, { replace: true })}
          className="text-[#555] hover:text-white text-xs inline-flex items-center gap-1"
          data-testid="skip-to-dashboard"
        >
          Skip <X size={12} />
        </button>
      </div>

      <div className="max-w-md mx-auto pt-12 pb-24 px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#9D4CDD]/15 border border-[#9D4CDD]/20 mb-3">
            <ConfettiIconWrap />
            <span className="text-[10px] uppercase tracking-wider font-semibold text-[#9D4CDD]">Shop Created</span>
          </div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Unbounded' }}>
            {ready && shop?.name ? `Welcome, ${shop.name}!` : 'Welcome!'}
          </h1>
          <p className="text-[#888] text-sm mt-2">
            Three quick things to get your shop discovered by local players.
          </p>
        </motion.div>

        {/* CTAs */}
        <div className="space-y-3">
          {ctas.map((c, i) => (
            <motion.button
              key={c.key}
              onClick={() => navigate(c.route)}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 + i * 0.08 }}
              className="w-full text-left bg-[#0e0e16] border border-white/[0.06] rounded-2xl p-4 hover:border-white/[0.18] hover:bg-white/[0.02] transition-all group"
              data-testid={`welcome-cta-${c.key}`}
            >
              <div className="flex items-center gap-3">
                <div
                  className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
                  style={{ backgroundColor: `${c.color}1f` }}
                >
                  <c.icon size={22} weight="duotone" style={{ color: c.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-semibold">{c.title}</p>
                  <p className="text-[#666] text-[11px] mt-0.5">{c.sub}</p>
                </div>
                <ArrowRight size={16} className="text-[#333] group-hover:text-white transition-colors" />
              </div>
            </motion.button>
          ))}
        </div>

        {/* Go to dashboard */}
        <motion.button
          onClick={() => navigate(`/shop/${shopId}`, { replace: true })}
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
          className="mt-8 w-full py-3 rounded-2xl bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.06] text-[#A1A1AA] text-sm font-medium transition-colors inline-flex items-center justify-center gap-2"
          data-testid="go-to-dashboard"
        >
          <Storefront size={14} weight="duotone" /> Go to dashboard
        </motion.button>

        <p className="text-center mt-6 text-[10px] text-[#444]">
          You can revisit these steps any time from the Shop menu.
        </p>
      </div>
    </div>
  );
}
